'use strict';

var index = require('../xdr/index.js');
var keypair = require('./keypair.js');
var address = require('./address.js');
var contract = require('./contract.js');
var index$1 = require('./numbers/index.js');
var xdr_large_int = require('./numbers/xdr_large_int.js');
var sc_int = require('./numbers/sc_int.js');
var scBytes = require('../xdr/generated/sc-bytes.js');
var scVal = require('../xdr/generated/sc-val.js');

function nativeToScVal(val, opts = {}) {
  switch (typeof val) {
    case "object": {
      if (val === null) {
        return scVal.ScVal.scvVoid();
      }
      if (scVal.ScVal.is(val)) {
        return val;
      }
      if (val instanceof address.Address) {
        return val.toScVal();
      }
      if (val instanceof keypair.Keypair) {
        return nativeToScVal(val.publicKey(), { type: "address" });
      }
      if (val instanceof contract.Contract) {
        return val.address().toScVal();
      }
      if (val instanceof Uint8Array) {
        const copy = Uint8Array.from(val);
        switch (opts?.type ?? "bytes") {
          case "bytes":
            return scVal.ScVal.scvBytes(new scBytes.ScBytes(copy));
          // Pass the bytes through unchanged — the string factories accept
          // Uint8Array, and decoding to a JS string first would mangle
          // non-UTF-8 content into U+FFFD replacement characters.
          case "symbol":
            return scVal.ScVal.scvSymbol(copy);
          case "string":
            return scVal.ScVal.scvString(copy);
          default:
            throw new TypeError(
              `invalid type (${JSON.stringify(opts.type)}) specified for bytes-like value`
            );
        }
      }
      if (Array.isArray(val)) {
        return scVal.ScVal.scvVec(
          val.map((v, idx) => {
            if (Array.isArray(opts.type)) {
              return nativeToScVal(
                v,
                // only include a `{ type: ... }` if it's present (safer than
                // `{type: undefined}`)
                {
                  ...opts.type.length > idx && {
                    type: opts.type[idx]
                  }
                }
              );
            }
            return nativeToScVal(v, opts);
          })
        );
      }
      if (val instanceof Map) {
        let uniformKeyType = null;
        let uniformValType = null;
        let perKeySpec = null;
        if (Array.isArray(opts.type)) {
          if (opts.type.length > 2) {
            throw new TypeError(
              `expected a [keyType, valType] pair for a Map, got ${JSON.stringify(opts.type)}`
            );
          }
          [uniformKeyType = null, uniformValType = null] = opts.type;
        } else if (typeof opts.type === "object" && opts.type !== null) {
          perKeySpec = opts.type;
        } else if (opts.type !== void 0) {
          throw new TypeError(
            `invalid type (${JSON.stringify(opts.type)}) specified for a Map`
          );
        }
        const entries = [];
        for (const [k, v] of val) {
          let keyType = uniformKeyType;
          let valType = uniformValType;
          if (perKeySpec && typeof k === "string" && Object.hasOwn(perKeySpec, k)) {
            [keyType = null, valType = null] = perKeySpec[k] ?? [];
          }
          entries.push(
            new scVal.ScMapEntry({
              key: nativeToScVal(k, keyType ? { type: keyType } : {}),
              val: nativeToScVal(v, valType ? { type: valType } : {})
            })
          );
        }
        return scvSortedMap(entries);
      }
      if (Object.getPrototypeOf(val) !== Object.prototype) {
        throw new TypeError(
          `cannot interpret ${val.constructor?.name} value as ScVal (${JSON.stringify(val)})`
        );
      }
      const mapTypeSpec = opts?.type ?? {};
      return scVal.ScVal.scvMap(
        Object.entries(val).sort(([key1], [key2]) => key1 < key2 ? -1 : key1 > key2 ? 1 : 0).map(([k, v]) => {
          const [keyType, valType] = Object.hasOwn(mapTypeSpec, k) ? mapTypeSpec[k] ?? [null, null] : [null, null];
          const keyOpts = keyType ? { type: keyType } : {};
          const valOpts = valType ? { type: valType } : {};
          return new scVal.ScMapEntry({
            key: nativeToScVal(k, keyOpts),
            val: nativeToScVal(v, valOpts)
          });
        })
      );
    }
    case "number":
    case "bigint": {
      const bigintVal = BigInt(val);
      switch (opts?.type) {
        case "u32":
          if (bigintVal < BigInt(index.Uint32.MIN_VALUE) || bigintVal > BigInt(index.Uint32.MAX_VALUE)) {
            throw new TypeError(`invalid value (${val}) for type u32`);
          }
          return scVal.ScVal.scvU32(Number(val));
        case "i32":
          if (bigintVal < BigInt(index.Int32.MIN_VALUE) || bigintVal > BigInt(index.Int32.MAX_VALUE)) {
            throw new TypeError(`invalid value (${val}) for type i32`);
          }
          return scVal.ScVal.scvI32(Number(val));
      }
      return new sc_int.ScInt(val, { type: opts?.type }).toScVal();
    }
    case "string": {
      const optType = opts?.type ?? "string";
      switch (optType) {
        case "string":
          return scVal.ScVal.scvString(val);
        case "symbol":
          return scVal.ScVal.scvSymbol(val);
        case "address":
          return new address.Address(val).toScVal();
        case "u32": {
          const bigintVal = BigInt(val);
          if (bigintVal < BigInt(index.Uint32.MIN_VALUE) || bigintVal > BigInt(index.Uint32.MAX_VALUE)) {
            throw new TypeError(`invalid value (${val}) for type u32`);
          }
          return scVal.ScVal.scvU32(Number(bigintVal));
        }
        case "i32": {
          const bigintVal = BigInt(val);
          if (bigintVal < BigInt(index.Int32.MIN_VALUE) || bigintVal > BigInt(index.Int32.MAX_VALUE)) {
            throw new TypeError(`invalid value (${val}) for type i32`);
          }
          return scVal.ScVal.scvI32(Number(bigintVal));
        }
        default:
          if (xdr_large_int.XdrLargeInt.isType(optType)) {
            return new xdr_large_int.XdrLargeInt(optType, val).toScVal();
          }
          throw new TypeError(
            `invalid type (${JSON.stringify(opts.type)}) specified for string value`
          );
      }
    }
    case "boolean":
      return scVal.ScVal.scvBool(val);
    case "undefined":
      return scVal.ScVal.scvVoid();
    case "function":
      return nativeToScVal(val());
    default:
      throw new TypeError(
        `failed to convert typeof ${typeof val} (${JSON.stringify(val)})`
      );
  }
}
function scValToNative(scv) {
  switch (scv.type) {
    case "scvVoid":
      return null;
    // these can be converted to bigints directly
    case "scvU64":
    case "scvI64":
      return scv.value;
    // these can be parsed by internal abstractions note that this can also
    // handle the above two cases, but it's not as efficient (another
    // type-check, parsing, etc.)
    case "scvU128":
    case "scvI128":
    case "scvU256":
    case "scvI256":
      return index$1.scValToBigInt(scv);
    case "scvVec":
      return (scv.value ?? []).map(scValToNative);
    case "scvAddress":
      return address.Address.fromScVal(scv).toString();
    case "scvMap":
      return Object.fromEntries(
        (scv.value ?? []).map((entry) => [
          scValToNative(entry.key),
          scValToNative(entry.val)
        ])
      );
    // these return the primitive type directly
    case "scvBool":
    case "scvU32":
    case "scvI32":
      return scv.value;
    case "scvBytes":
      return scv.value.value;
    // Symbols are limited to [a-zA-Z0-9_]+, so we can safely make ascii strings
    //
    // Strings, however, are "presented" as strings and we treat them as such
    // (in other words, string = bytes with a hint that it's text). If the user
    // encoded non-printable bytes in their string value, that's on them.
    //
    // Note that we assume a utf8 encoding (ascii-compatible). For other
    // encodings, you should probably use bytes anyway. If it cannot be decoded,
    // the raw bytes are returned.
    case "scvSymbol":
      return scv.sym.asStringOrBytes();
    case "scvString":
      return scv.str.asStringOrBytes();
    case "scvExecutableTag":
      return scv.executableTag.asStringOrBytes();
    // these can be converted to bigint
    case "scvTimepoint":
    case "scvDuration":
      return scv.value;
    case "scvError": {
      const err = scv.value;
      if (err.type === "sceContract") {
        return { type: "contract", code: err.value };
      }
      const code = err.value;
      return {
        type: "system",
        code: code.value,
        value: code.name
      };
    }
    case "scvContractInstance":
    case "scvLedgerKeyNonce":
      return scv.value;
    case "scvLedgerKeyContractInstance":
      return void 0;
    default:
      return scv.value;
  }
}
function scvSortedMap(items) {
  const sorted = Array.from(items).sort((a, b) => {
    const nativeA = scValToNative(a.key);
    const nativeB = scValToNative(b.key);
    switch (typeof nativeA) {
      case "number":
      case "bigint":
        if (nativeA === nativeB) return 0;
        return nativeA < nativeB ? -1 : 1;
      default: {
        const strA = nativeA.toString();
        const strB = nativeB.toString();
        return strA < strB ? -1 : strA > strB ? 1 : 0;
      }
    }
  });
  return scVal.ScVal.scvMap(sorted);
}

exports.nativeToScVal = nativeToScVal;
exports.scValToNative = scValToNative;
exports.scvSortedMap = scvSortedMap;
//# sourceMappingURL=scval.js.map
